/* Connection Composable - Manages ESP32 connection state via polling */
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { deviceApi } from '@/api/client'
import { useUiStore } from '@/stores/ui'

export type ConnectionState = 'connected' | 'disconnected' | 'error' | 'checking'

export interface ConnectionStatus {
  state: ConnectionState
  lastCheck: number | null
  lastSuccess: number | null
  errorMessage: string | null
  isDismissed: boolean
}

const POLLING_INTERVAL = 30000 // 30 seconds
const CHECK_TIMEOUT = 5000 // 5 seconds

const status = ref<ConnectionStatus>({
  state: 'checking',
  lastCheck: null,
  lastSuccess: null,
  errorMessage: null,
  isDismissed: false,
})

let pollingTimer: ReturnType<typeof setInterval> | null = null
let abortController: AbortController | null = null

export function useConnection() {
  const uiStore = useUiStore()

  async function checkConnection(): Promise<ConnectionState> {
    abortController = new AbortController()
    const timeoutId = setTimeout(() => abortController?.abort(), CHECK_TIMEOUT)

    try {
      // Try to fetch devices as a health check
      await deviceApi.list()
      clearTimeout(timeoutId)
      return 'connected'
    } catch (e: any) {
      clearTimeout(timeoutId)
      if (e.name === 'AbortError' || e.name === 'TimeoutError') {
        return 'error'
      }
      // Network error or 4xx/5xx
      return 'error'
    }
  }

  async function runCheck() {
    const newState = await checkConnection()
    const now = Date.now()

    status.value.lastCheck = now
    status.value.state = newState
    status.value.errorMessage = newState === 'error' ? 'No se pudo conectar al dispositivo' : null

    if (newState === 'connected') {
      status.value.lastSuccess = now
    }

    // Re-show banner on state change if it was dismissed
    if (status.value.isDismissed && newState !== 'connected') {
      status.value.isDismissed = false
    }
  }

  function dismiss() {
    status.value.isDismissed = true
  }

  function retry() {
    status.value.isDismissed = false
    status.value.state = 'checking'
    runCheck()
  }

  function startPolling() {
    if (pollingTimer) return
    runCheck() // Initial check
    pollingTimer = setInterval(runCheck, POLLING_INTERVAL)
  }

  function stopPolling() {
    if (pollingTimer) {
      clearInterval(pollingTimer)
      pollingTimer = null
    }
    if (abortController) {
      abortController.abort()
      abortController = null
    }
  }

  // Auto-start/stop based on authentication
  watch(
    () => uiStore.modals,
    () => {
      // We'll control this from the component
    }
  )

  onUnmounted(() => {
    stopPolling()
  })

  return {
    status,
    checkConnection: runCheck,
    dismiss,
    retry,
    startPolling,
    stopPolling,
  }
}